/**
 * Notify the parent window that auth state changed.
 *
 * The SPA is embedded in the marketing-site `concierge.html` via iframe.
 * On sign-in (any flow — OTP, Google, passkey, magic-link) and sign-out,
 * we tell the parent so the Cordiant widget can re-fetch
 * `/api/guest/context` and re-render the marketing chrome (Discover FAB,
 * top strip, profile avatar) against the new auth state.
 *
 * The parent listens for `{ type: 'cordiant:auth-success' }` or
 * `{ type: 'cordiant:auth-signed-out' }` and calls `window.cordiant.hydrate()`
 * after clearing its sessionStorage cache.
 *
 * No-op when the SPA is at top level (not iframed).
 *
 * Targets every known marketing-site origin once (`postMessage` takes a
 * single `targetOrigin`, so we emit per-origin) — keeps the payload from
 * leaking to a hostile embedder.
 *
 * Marketing is served from the apex `zenpacorlando.com`. A stray
 * `web.zenpacorlando.com` entry here isn't harmful (the browser discards
 * the post to the non-matching origin) but it logs a noisy
 * "target origin does not match" error to every guest's console on each
 * auth/language handshake, so we keep the list to origins that are really
 * used. Re-add a host here if marketing is ever served from it.
 */
export const PARENT_TARGET_ORIGINS = [
  'https://zenpacorlando.com',
];

function emit(type: 'cordiant:auth-success' | 'cordiant:auth-signed-out'): void {
  if (typeof window === 'undefined') return;
  if (window.parent === window) return; // not iframed
  const payload = { type, ts: Date.now() };
  // Each postMessage in its own try/catch — postMessage throws when the
  // target origin doesn't match the recipient window's origin, and a
  // single try/catch around the loop swallows the throw AND skips every
  // remaining origin. That means one bad origin in the list silences the
  // whole notification. We want the browser to reject the ones that
  // don't match and deliver the one that does.
  for (const origin of PARENT_TARGET_ORIGINS) {
    try {
      window.parent.postMessage(payload, origin);
    } catch {
      // Origin mismatch is expected for every origin in the list except
      // the one that actually matches the parent. Silent — the matching
      // origin's post succeeded.
    }
  }
}

export function notifyParentAuthSuccess(): void {
  emit('cordiant:auth-success');
}

export function notifyParentSignedOut(): void {
  emit('cordiant:auth-signed-out');
}

/**
 * Notify the marketing parent that a workflow modal closed, with the
 * backend-reported `outcome`. The parent (ConciergeWorkflowBridge) uses this
 * together with a returnTo it stashed when a CTA launched the workflow:
 * `cancelled` → navigate back to the originating page; `completed` → just
 * clear the stashed returnTo and stay on the success card.
 *
 * Fired on every workflow close — harmless when there's no stashed returnTo
 * (guest opened the workflow from chat, not from a marketing CTA) and a no-op
 * when the SPA isn't iframed.
 */
export function notifyParentWorkflowClosed(outcome: 'completed' | 'cancelled'): void {
  if (typeof window === 'undefined') return;
  if (window.parent === window) return; // not iframed
  const payload = { type: 'cordiant:workflow-closed' as const, outcome, ts: Date.now() };
  for (const origin of PARENT_TARGET_ORIGINS) {
    try {
      window.parent.postMessage(payload, origin);
    } catch {
      // Origin mismatch is expected for every origin except the real parent.
    }
  }
}
